import React, { useEffect, useRef, useState } from "react";
import { getFirestore, collection, getDocs, doc, updateDoc, deleteDoc, writeBatch } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { MdDragIndicator } from "react-icons/md";
import { FiEdit2, FiTrash2, FiUpload, FiDownload, FiArchive, FiRotateCcw, FiAlertTriangle, FiCode, FiFilm, FiExternalLink } from "react-icons/fi";
import DocViewerModal from "../components/DocViewerModal";
import VideoExportModal from "../components/VideoExportModal";
import { typePositions } from "../utils/lessonNumbering";

const REQUIRED_LESSON_FIELDS = ["title", "concept", "type"];

function normalizeLessonList(parsed) {
  if (Array.isArray(parsed)) return parsed;
  if (parsed && Array.isArray(parsed.lessons)) return parsed.lessons;
  if (parsed && typeof parsed === "object") return [parsed];
  return [];
}

const TYPE_COLORS = {
  basics:              { bg: "#2a9d8f", border: "#258D80" },
  listening:           { bg: "#e76f51", border: "#cf6348" },
  "listening to english": { bg: "#e76f51", border: "#cf6348" },
  tracing:             { bg: "#e9c46a", border: "#d4b15f" },
  story:               { bg: "#f4a261", border: "#dd8f50" },
  "story review":      { bg: "#f4a261", border: "#dd8f50" },
  writing:             { bg: "#ff7f9a", border: "#e36782" },
  match:               { bg: "#9b59b6", border: "#8748a2" },
  "match to english":  { bg: "#9b59b6", border: "#8748a2" },
  speaking:            { bg: "#50C878", border: "#46B06A" },
  test:                { bg: "#3a82c6", border: "#317aa8" },
};

function typeStyle(type) {
  return TYPE_COLORS[(type || "").toLowerCase()] || { bg: "#cccccc", border: "#B7B7B7" };
}

function Lessons() {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [search, setSearch] = useState("");
  const [questionFilter, setQuestionFilter] = useState("");
  const [conceptFilter, setConceptFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [contentStatusFilter, setContentStatusFilter] = useState("");
  const [savingOrder, setSavingOrder] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [viewDoc, setViewDoc] = useState(null);
  const [exportLesson, setExportLesson] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [statusUpdating, setStatusUpdating] = useState(false);
  const fileInputRef = useRef(null);
  const db = getFirestore();

  useEffect(() => {
    fetchLessons();
    // eslint-disable-next-line
  }, []);

  const fetchLessons = async () => {
    const snap = await getDocs(collection(db, "lessons"));
    setLessons(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    setImporting(true);
    setImportResult(null);

    const parsedEntries = [];
    const errors = [];

    for (const file of files) {
      let parsed;
      try {
        parsed = JSON.parse(await file.text());
      } catch (err) {
        errors.push(`${file.name}: invalid JSON (${err.message})`);
        continue;
      }
      const list = normalizeLessonList(parsed);
      if (list.length === 0) {
        errors.push(`${file.name}: no lessons found`);
        continue;
      }
      list.forEach((lesson, i) => {
        const label = lesson?.title || `${file.name} #${i + 1}`;
        const missing = REQUIRED_LESSON_FIELDS.filter((f) => !lesson?.[f]);
        if (missing.length > 0) {
          errors.push(`"${label}": missing ${missing.join(", ")}`);
          return;
        }
        parsedEntries.push(lesson);
      });
    }

    const lessonsById = new Map(lessons.map((l) => [l.id, l]));
    const matchedIds = new Set();

    const buildLessonData = (lesson, existing) => {
      const lessonData = {
        title: String(lesson.title),
        concept: String(lesson.concept),
        type: String(lesson.type),
        cups: Number(lesson.cups) || 0,
        description: lesson.description || "",
        questions: Array.isArray(lesson.questions) ? lesson.questions : [],
        archived: typeof lesson.archived === "boolean" ? lesson.archived : !!existing?.archived,
        status: typeof lesson.status === "string" ? lesson.status : (existing?.status || "published"),
      };
      if (lesson.type === "Basics" && lesson.script) {
        lessonData.script = String(lesson.script);
      }
      return lessonData;
    };

    // Sort by each entry's "number" field (falling back to its position in the file when the
    // number is missing/tied) so editing "number" values in the JSON reorders lessons even if
    // the entries themselves weren't physically moved within the array.
    const orderedEntries = parsedEntries
      .map((lesson, idx) => ({ lesson, idx }))
      .sort((a, b) => {
        const an = Number(a.lesson.number);
        const bn = Number(b.lesson.number);
        const aHas = Number.isFinite(an);
        const bHas = Number.isFinite(bn);
        if (aHas && bHas && an !== bn) return an - bn;
        if (aHas !== bHas) return aHas ? -1 : 1;
        return a.idx - b.idx;
      })
      .map((e) => e.lesson);

    // The resulting order becomes the new lesson order. An entry whose "id" matches an existing
    // lesson updates that lesson in place; entries with no matching id are created as new
    // lessons at that position.
    const plan = orderedEntries.map((lesson) => {
      const existing = lesson.id ? lessonsById.get(lesson.id) : undefined;
      if (existing) matchedIds.add(existing.id);
      return { id: existing ? existing.id : null, data: buildLessonData(lesson, existing) };
    });

    // Lessons not mentioned in the file keep their content untouched, just shifted after the
    // imported set (in their original relative order) to make room.
    const leftovers = lessons
      .filter((l) => !matchedIds.has(l.id))
      .sort((a, b) => (a.number || 0) - (b.number || 0));
    leftovers.forEach((l) => plan.push({ id: l.id, data: null, prevNumber: l.number || 0 }));

    plan.forEach((item, i) => { item.number = i + 1; });

    let createCount = 0;
    let updateCount = 0;
    let renumberedCount = 0;
    const CHUNK_SIZE = 400; // stay under Firestore's 500-write batch limit
    for (let i = 0; i < plan.length; i += CHUNK_SIZE) {
      const chunk = plan.slice(i, i + CHUNK_SIZE);
      const batch = writeBatch(db);
      const writes = [];
      chunk.forEach((item) => {
        if (!item.id) {
          batch.set(doc(collection(db, "lessons")), { ...item.data, number: item.number });
          writes.push("create");
        } else if (item.data) {
          batch.update(doc(db, "lessons", item.id), { ...item.data, number: item.number });
          writes.push("update");
        } else if (item.number !== item.prevNumber) {
          batch.update(doc(db, "lessons", item.id), { number: item.number });
          writes.push("renumber");
        }
      });
      if (writes.length === 0) continue;
      try {
        await batch.commit();
        for (const w of writes) {
          if (w === "create") createCount++;
          else if (w === "update") updateCount++;
          else renumberedCount++;
        }
      } catch (err) {
        errors.push(`Batch starting at #${i + 1}: ${err.message}`);
      }
    }

    if (createCount + updateCount + renumberedCount > 0) await fetchLessons();
    setImportResult({ createCount, updateCount, renumberedCount, total: parsedEntries.length, errors });
    setImporting(false);
  };

  const handleExportJson = () => {
    const sorted = Array.from(lessons).sort((a, b) => (a.number || 0) - (b.number || 0));
    const blob = new Blob([JSON.stringify(sorted, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `lessons-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    setDeleteError("");
    try {
      await deleteDoc(doc(db, "lessons", id));
      setLessons((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      setDeleteError(err?.code === "permission-denied"
        ? "Permission denied. Your CMS admin account needs delete access to lessons."
        : `Delete failed: ${err.message}`);
    }
  };

  const handleArchiveToggle = async (id, archived) => {
    await updateDoc(doc(db, "lessons", id), { archived: !archived });
    setLessons((prev) => prev.map((l) => (l.id === id ? { ...l, archived: !archived } : l)));
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkStatus = async (newStatus) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setStatusUpdating(true);
    setDeleteError("");
    const CHUNK_SIZE = 400; // stay under Firestore's 500-write batch limit
    try {
      for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
        const batch = writeBatch(db);
        ids.slice(i, i + CHUNK_SIZE).forEach((id) => {
          batch.update(doc(db, "lessons", id), { status: newStatus });
        });
        await batch.commit();
      }
      const idSet = new Set(ids);
      setLessons((prev) => prev.map((l) => (idSet.has(l.id) ? { ...l, status: newStatus } : l)));
      clearSelection();
    } catch (err) {
      setDeleteError(err?.code === "permission-denied"
        ? "Permission denied. Your CMS admin account needs write access to lessons."
        : `Status update failed: ${err.message}`);
    }
    setStatusUpdating(false);
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = Array.from(filteredLessons);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    // When filtered (e.g. Basics only), reuse the visible lessons' existing number slots so
    // the reorder doesn't collide with lessons hidden by the filter.
    const slots = hasFilters
      ? filteredLessons.map((l) => Number(l.number) || 0).sort((a, b) => a - b)
      : reordered.map((_, i) => i + 1);
    const distinctSlots = new Set(slots).size === slots.length && !slots.includes(0);
    const targetNumbers = distinctSlots ? slots : reordered.map((_, i) => i + 1);

    setSavingOrder(true);
    const updates = reordered
      .map((lesson, i) =>
        lesson.number !== targetNumbers[i]
          ? updateDoc(doc(db, "lessons", lesson.id), { number: targetNumbers[i] })
          : null
      )
      .filter(Boolean);
    await Promise.all(updates);
    await fetchLessons();
    setSavingOrder(false);
  };

  const uniqueConcepts = Array.from(new Set(lessons.map((l) => l.concept).filter(Boolean))).sort();
  const TYPE_ORDER = ["Basics", "Listening", "Tracing", "Story", "Writing", "Match", "Speaking", "Test"];
  const uniqueTypes = Array.from(
    new Set([...lessons.map((l) => l.type).filter(Boolean), "Speaking"])
  ).sort((a, b) => {
    const ai = TYPE_ORDER.indexOf(a);
    const bi = TYPE_ORDER.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.localeCompare(b);
  });

  const filteredLessons = lessons
    .filter((lesson) => {
      const matchesSearch =
        lesson.title?.toLowerCase().includes(search.toLowerCase()) ||
        lesson.concept?.toLowerCase().includes(search.toLowerCase());

      const qCount = Array.isArray(lesson.questions) ? lesson.questions.length : 0;
      let matchesQuestions = true;
      if (questionFilter === "0") matchesQuestions = qCount === 0;
      else if (questionFilter === "1-5") matchesQuestions = qCount >= 1 && qCount <= 5;
      else if (questionFilter === "6+") matchesQuestions = qCount >= 6;

      const matchesConcept = conceptFilter ? lesson.concept === conceptFilter : true;
      const matchesType = typeFilter ? lesson.type === typeFilter : true;
      const matchesStatus =
        statusFilter === "all" ? true : statusFilter === "archived" ? !!lesson.archived : !lesson.archived;
      const lessonContentStatus = lesson.status || "published";
      const matchesContentStatus = contentStatusFilter ? lessonContentStatus === contentStatusFilter : true;

      return matchesSearch && matchesQuestions && matchesConcept && matchesType && matchesStatus && matchesContentStatus;
    })
    .sort((a, b) => (a.number || 0) - (b.number || 0));

  const positionsInType = typePositions(lessons);
  const archivedCount = lessons.filter((l) => l.archived).length;
  const draftCount = lessons.filter((l) => l.status === "draft").length;

  const visibleIds = filteredLessons.map((l) => l.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const BULK_STATUS_ACTIONS = [
    { label: "Mark Draft", status: "draft", bg: "#e9c46a", border: "#d4b15f" },
    { label: "Mark Beta", status: "beta", bg: "#9b59b6", border: "#8748a2" },
    { label: "Mark Published", status: "published", bg: "#2a9d8f", border: "#258D80" },
  ];

  const hasFilters = search || questionFilter || conceptFilter || typeFilter || statusFilter !== "active" || contentStatusFilter;
  const resetFilters = () => {
    setSearch("");
    setQuestionFilter("");
    setConceptFilter("");
    setTypeFilter("");
    setStatusFilter("active");
    setContentStatusFilter("");
  };

  const selectStyle = {
    fontFamily: "'Quicksand', sans-serif",
    fontSize: 14,
    fontWeight: 600,
    padding: "9px 12px",
    borderRadius: 10,
    border: "1.5px solid #E0E0E0",
    background: "#fff",
    color: "#000",
    cursor: "pointer",
    outline: "none",
    flex: "0 1 160px",
    minWidth: 130,
  };

  return (
    <div style={{ fontFamily: "'Quicksand', sans-serif", maxWidth: 1200 }}>

      {/* ── Page Header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Lessons</h1>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#606060",
              marginTop: 4,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span>
              {filteredLessons.length} lesson{filteredLessons.length !== 1 ? "s" : ""}
              {hasFilters ? " matching filters" : " total"}
              {archivedCount > 0 && statusFilter === "active" && ` (${archivedCount} archived hidden)`}
              {draftCount > 0 && !hasFilters && ` · ${draftCount} draft${draftCount !== 1 ? "s" : ""}`}
            </span>
            {savingOrder && (
              <span
                style={{
                  background: "#e9c46a22",
                  color: "#b8890a",
                  border: "1px solid #e9c46a",
                  borderRadius: 8,
                  padding: "2px 10px",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                Saving order…
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="file"
            accept="application/json,.json"
            multiple
            ref={fileInputRef}
            onChange={handleImportFiles}
            style={{ display: "none" }}
          />
          <button
            className="primary-button"
            onClick={handleImportClick}
            disabled={importing}
            title="Import lessons from JSON file(s)"
            style={{
              backgroundColor: "#fff",
              color: "#2a9d8f",
              borderColor: "#2a9d8f",
              fontSize: 14,
              padding: "10px 18px",
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              cursor: importing ? "not-allowed" : "pointer",
              opacity: importing ? 0.6 : 1,
            }}
          >
            <FiUpload size={15} /> {importing ? "Importing…" : "Import JSON"}
          </button>
          <button
            className="primary-button"
            onClick={handleExportJson}
            disabled={lessons.length === 0}
            title="Download all lessons as JSON"
            style={{
              backgroundColor: "#fff",
              color: "#2a9d8f",
              borderColor: "#2a9d8f",
              fontSize: 14,
              padding: "10px 18px",
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              cursor: lessons.length === 0 ? "not-allowed" : "pointer",
              opacity: lessons.length === 0 ? 0.6 : 1,
            }}
          >
            <FiDownload size={15} /> Download JSON
          </button>
          <button
            className="primary-button"
            onClick={() => navigate("/addlesson")}
            style={{
              backgroundColor: "#2a9d8f",
              color: "#fff",
              borderColor: "#258D80",
              fontSize: 14,
              padding: "10px 22px",
            }}
          >
            + Add Lesson
          </button>
        </div>
      </div>

      {deleteError && (
        <div style={{ color: "#e76f51", fontSize: 13, fontWeight: 600, marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
          <FiAlertTriangle size={14} /> {deleteError}
        </div>
      )}

      {/* ── Import Result Banner ── */}
      {importResult && (
        <div
          style={{
            background: importResult.errors.length > 0 ? "#fff8e6" : "#eafaf3",
            border: `1px solid ${importResult.errors.length > 0 ? "#e9c46a" : "#50C878"}`,
            borderRadius: 12,
            padding: "12px 16px",
            marginBottom: 20,
            fontFamily: "'Quicksand', sans-serif",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#333" }}>
              {(() => {
                const parts = [];
                if (importResult.createCount > 0) parts.push(`${importResult.createCount} added`);
                if (importResult.updateCount > 0) parts.push(`${importResult.updateCount} updated`);
                let msg = parts.length > 0
                  ? `${parts.join(", ")} (of ${importResult.total} in file)`
                  : "No lessons added or updated.";
                if (importResult.renumberedCount > 0) {
                  msg += `; ${importResult.renumberedCount} other lesson${importResult.renumberedCount !== 1 ? "s" : ""} renumbered to make room`;
                }
                if (importResult.errors.length > 0) {
                  msg += ` ${importResult.errors.length} issue${importResult.errors.length !== 1 ? "s" : ""} skipped.`;
                }
                return msg;
              })()}
            </span>
            <button
              onClick={() => setImportResult(null)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#888", fontWeight: 700, fontSize: 14, flexShrink: 0 }}
            >
              ✕
            </button>
          </div>
          {importResult.errors.length > 0 && (
            <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: "#b8890a", fontSize: 12.5, fontWeight: 600 }}>
              {importResult.errors.slice(0, 10).map((err, i) => <li key={i}>{err}</li>)}
              {importResult.errors.length > 10 && <li>…and {importResult.errors.length - 10} more</li>}
            </ul>
          )}
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #E0E0E0",
          padding: "14px 18px",
          marginBottom: 20,
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {/* Search */}
        <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
          <span
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#aaa",
              pointerEvents: "none",
              fontSize: 15,
            }}
          >
            🔍
          </span>
          <input
            type="text"
            placeholder="Search by title or concept…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              fontFamily: "'Quicksand', sans-serif",
              fontSize: 14,
              fontWeight: 500,
              padding: "9px 12px 9px 36px",
              borderRadius: 10,
              border: "1.5px solid #E0E0E0",
              background: "#fff",
              color: "#000",
              outline: "none",
              width: "100%",
            }}
          />
        </div>

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={selectStyle}>
          <option value="active">Active Lessons</option>
          <option value="archived">Archived Only</option>
          <option value="all">All Lessons</option>
        </select>

        <select value={contentStatusFilter} onChange={(e) => setContentStatusFilter(e.target.value)} style={selectStyle}>
          <option value="">Any Status</option>
          <option value="draft">Draft</option>
          <option value="beta">Beta</option>
          <option value="published">Published</option>
        </select>

        <select value={conceptFilter} onChange={(e) => setConceptFilter(e.target.value)} style={selectStyle}>
          <option value="">All Concepts</option>
          {uniqueConcepts.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        <select value={questionFilter} onChange={(e) => setQuestionFilter(e.target.value)} style={selectStyle}>
          <option value="">Any # of Questions</option>
          <option value="0">0 Questions</option>
          <option value="1-5">1–5 Questions</option>
          <option value="6+">6+ Questions</option>
        </select>

        {hasFilters && (
          <button
            onClick={resetFilters}
            style={{
              background: "none",
              border: "1.5px solid #E0E0E0",
              borderRadius: 10,
              padding: "9px 14px",
              fontFamily: "'Quicksand', sans-serif",
              fontWeight: 700,
              fontSize: 13,
              color: "#606060",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* ── Type Chips ── */}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        {[{ value: "", label: "All Types", bg: "#2a9d8f", border: "#258D80" }, ...uniqueTypes.map((t) => ({ value: t, label: t, ...typeStyle(t) }))].map(
          (chip) => {
            const isActive = typeFilter === chip.value;
            return (
              <button
                key={chip.value || "__all__"}
                onClick={() => setTypeFilter(isActive ? "" : chip.value)}
                style={{
                  fontFamily: "'Quicksand', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  padding: "8px 16px",
                  borderRadius: 999,
                  border: `1.5px solid ${chip.border}`,
                  borderBottomWidth: isActive ? 2 : 4,
                  background: isActive ? chip.bg : "#fff",
                  color: isActive ? "#fff" : chip.bg,
                  cursor: "pointer",
                  transition: "transform 80ms ease, border-bottom-width 80ms ease",
                  whiteSpace: "nowrap",
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = "translateY(2px)";
                  e.currentTarget.style.borderBottomWidth = "1.5px";
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = "";
                  e.currentTarget.style.borderBottomWidth = isActive ? "2px" : "4px";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "";
                  e.currentTarget.style.borderBottomWidth = isActive ? "2px" : "4px";
                }}
              >
                {chip.label}
              </button>
            );
          }
        )}
      </div>

      {/* ── Bulk Status Bar ── */}
      {selectedIds.size > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            background: "#eafaf3",
            border: "1.5px solid #50C878",
            borderRadius: 12,
            padding: "12px 16px",
            marginBottom: 20,
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 13, color: "#333" }}>
            {selectedIds.size} selected
          </span>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {BULK_STATUS_ACTIONS.map((a) => (
              <button
                key={a.status}
                className="primary-button"
                disabled={statusUpdating}
                onClick={() => handleBulkStatus(a.status)}
                style={{
                  backgroundColor: a.bg,
                  color: "#fff",
                  borderColor: a.border,
                  fontSize: 13,
                  padding: "8px 16px",
                  cursor: statusUpdating ? "not-allowed" : "pointer",
                  opacity: statusUpdating ? 0.6 : 1,
                }}
              >
                {a.label}
              </button>
            ))}
          </div>
          {statusUpdating && (
            <span style={{ fontSize: 12, fontWeight: 700, color: "#2a9d8f" }}>Updating…</span>
          )}
          <button
            onClick={clearSelection}
            style={{
              marginLeft: "auto",
              background: "none",
              border: "1.5px solid #E0E0E0",
              borderRadius: 10,
              padding: "8px 14px",
              fontFamily: "'Quicksand', sans-serif",
              fontWeight: 700,
              fontSize: 13,
              color: "#606060",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            ✕ Clear selection
          </button>
        </div>
      )}

      {/* ── Table ── */}
      {filteredLessons.length === 0 ? (
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #E0E0E0",
            padding: "48px 24px",
            textAlign: "center",
            color: "#606060",
            fontWeight: 600,
            fontSize: 15,
          }}
        >
          {hasFilters ? "No lessons match your filters." : "No lessons yet — add your first one!"}
        </div>
      ) : (
        <div
          style={{
            borderRadius: 16,
            overflow: "auto",
            border: "1.5px solid #E0E0E0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="lessons-droppable">
              {(provided) => (
                <table
                  style={{
                    width: "100%",
                    minWidth: 720,
                    borderCollapse: "collapse",
                    fontFamily: "'Quicksand', sans-serif",
                    fontSize: 14,
                    background: "#fff",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#2a9d8f" }}>
                      <th style={{ width: 40, padding: "12px 4px 12px 16px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected;
                          }}
                          onChange={toggleSelectAll}
                          title="Select all lessons in view"
                          style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#fff" }}
                        />
                      </th>
                      <th style={{ width: 28, padding: "12px 8px" }} />
                      <th
                        style={{
                          width: 48,
                          padding: "12px 8px",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 12,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          textAlign: "left",
                        }}
                      >
                        #
                      </th>
                      <th
                        style={{
                          padding: "12px 16px",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 12,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          textAlign: "left",
                        }}
                      >
                        Title
                      </th>
                      <th
                        style={{
                          padding: "12px 16px",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 12,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          textAlign: "left",
                        }}
                      >
                        Concept
                      </th>
                      <th
                        style={{
                          width: 130,
                          padding: "12px 16px",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 12,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          textAlign: "left",
                        }}
                      >
                        Type
                      </th>
                      <th
                        style={{
                          width: 56,
                          padding: "12px 8px",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 12,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          textAlign: "center",
                        }}
                      >
                        Qs
                      </th>
                      <th
                        style={{
                          width: 230,
                          padding: "12px 16px",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 12,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          textAlign: "right",
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {filteredLessons.map((lesson, idx) => {
                      const { bg } = typeStyle(lesson.type);
                      const qCount = Array.isArray(lesson.questions) ? lesson.questions.length : 0;

                      return (
                        <Draggable key={lesson.id} draggableId={lesson.id} index={idx}>
                          {(provided, snapshot) => (
                            <tr
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                background: snapshot.isDragging ? "#f0faf9" : "#fff",
                                borderBottom: "1px solid #f3f3f3",
                                opacity: lesson.archived ? 0.55 : 1,
                              }}
                            >
                              {/* Select checkbox */}
                              <td style={{ padding: "0 4px 0 16px", width: 40, textAlign: "center" }}>
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(lesson.id)}
                                  onChange={() => toggleSelect(lesson.id)}
                                  title="Select lesson"
                                  style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#2a9d8f" }}
                                />
                              </td>

                              {/* Drag handle */}
                              <td
                                {...provided.dragHandleProps}
                                style={{
                                  padding: "0 4px 0 6px",
                                  color: "#ccc",
                                  cursor: "grab",
                                  width: 28,
                                }}
                              >
                                <MdDragIndicator size={18} />
                              </td>

                              {/* Number */}
                              <td
                                style={{
                                  padding: "14px 8px",
                                  fontWeight: 700,
                                  color: "#aaa",
                                  fontSize: 13,
                                  width: typeFilter ? 84 : 48,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {lesson.number || idx + 1}
                                {typeFilter && positionsInType[lesson.id] != null && (
                                  <span
                                    title={`${lesson.type} lesson #${positionsInType[lesson.id]}`}
                                    style={{
                                      display: "inline-block",
                                      marginLeft: 6,
                                      padding: "1px 6px",
                                      borderRadius: 8,
                                      background: bg,
                                      color: "#fff",
                                      fontSize: 11,
                                      fontWeight: 700,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    #{positionsInType[lesson.id]}
                                  </span>
                                )}
                              </td>

                              {/* Title */}
                              <td style={{ padding: "14px 16px", fontWeight: 700, fontSize: 14, maxWidth: 240 }}>
                                <div
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {lesson.title || "Untitled"}
                                </div>
                                <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                                  {lesson.status === "draft" && (
                                    <span
                                      style={{
                                        display: "inline-block",
                                        background: "#fff2d6",
                                        color: "#b8890a",
                                        borderRadius: 999,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        padding: "2px 8px",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.04em",
                                      }}
                                    >
                                      Draft
                                    </span>
                                  )}
                                  {lesson.status === "beta" && (
                                    <span
                                      style={{
                                        display: "inline-block",
                                        background: "#f0e6f9",
                                        color: "#8748a2",
                                        borderRadius: 999,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        padding: "2px 8px",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.04em",
                                      }}
                                    >
                                      Beta
                                    </span>
                                  )}
                                  {lesson.archived && (
                                    <span
                                      style={{
                                        display: "inline-block",
                                        background: "#eee",
                                        color: "#888",
                                        borderRadius: 999,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        padding: "2px 8px",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.04em",
                                      }}
                                    >
                                      Archived
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Concept */}
                              <td
                                style={{
                                  padding: "14px 16px",
                                  color: "#606060",
                                  fontSize: 13,
                                  fontWeight: 500,
                                  maxWidth: 200,
                                }}
                              >
                                <div
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {lesson.concept || "—"}
                                </div>
                              </td>

                              {/* Type badge */}
                              <td style={{ padding: "14px 16px", width: 130 }}>
                                {lesson.type && (
                                  <span
                                    style={{
                                      background: bg,
                                      color: "#fff",
                                      borderRadius: 999,
                                      fontSize: 12,
                                      fontWeight: 700,
                                      padding: "4px 12px",
                                      textTransform: "capitalize",
                                      whiteSpace: "nowrap",
                                      fontFamily: "'Quicksand', sans-serif",
                                    }}
                                  >
                                    {lesson.type}
                                  </span>
                                )}
                              </td>

                              {/* Question count */}
                              <td style={{ padding: "14px 8px", textAlign: "center", width: 56 }}>
                                <span
                                  style={{
                                    fontWeight: 700,
                                    fontSize: 14,
                                    color: qCount === 0 ? "#ccc" : "#2a9d8f",
                                  }}
                                >
                                  {qCount}
                                </span>
                              </td>

                              {/* Actions */}
                              <td style={{ padding: "14px 16px", width: 230 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 8,
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                  }}
                                >
                                  <button
                                    onClick={() => setViewDoc({ path: `lessons/${lesson.id}`, data: lesson })}
                                    title="View Firestore document"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: 34,
                                      height: 34,
                                      borderRadius: 10,
                                      border: "1.5px solid #3a82c6",
                                      borderBottomWidth: 3,
                                      background: "#fff",
                                      color: "#3a82c6",
                                      cursor: "pointer",
                                      transition: "transform 80ms ease, border-bottom-width 80ms ease",
                                      flexShrink: 0,
                                    }}
                                    onMouseDown={(e) => {
                                      e.currentTarget.style.transform = "translateY(2px)";
                                      e.currentTarget.style.borderBottomWidth = "1.5px";
                                    }}
                                    onMouseUp={(e) => {
                                      e.currentTarget.style.transform = "";
                                      e.currentTarget.style.borderBottomWidth = "3px";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = "";
                                      e.currentTarget.style.borderBottomWidth = "3px";
                                    }}
                                  >
                                    <FiCode size={15} />
                                  </button>
                                  {lesson.type === "Basics" && (
                                    <button
                                      onClick={() => setExportLesson(lesson)}
                                      title="Export as video"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 34,
                                        height: 34,
                                        borderRadius: 10,
                                        border: "1.5px solid #2a9d8f",
                                        borderBottomWidth: 3,
                                        background: "#fff",
                                        color: "#2a9d8f",
                                        cursor: "pointer",
                                        transition: "transform 80ms ease, border-bottom-width 80ms ease",
                                        flexShrink: 0,
                                      }}
                                      onMouseDown={(e) => {
                                        e.currentTarget.style.transform = "translateY(2px)";
                                        e.currentTarget.style.borderBottomWidth = "1.5px";
                                      }}
                                      onMouseUp={(e) => {
                                        e.currentTarget.style.transform = "";
                                        e.currentTarget.style.borderBottomWidth = "3px";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "";
                                        e.currentTarget.style.borderBottomWidth = "3px";
                                      }}
                                    >
                                      <FiFilm size={15} />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => window.open(`/lessons/${lesson.id}/edit`, "_blank", "noopener,noreferrer")}
                                    title="Open lesson in new tab"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: 34,
                                      height: 34,
                                      borderRadius: 10,
                                      border: "1.5px solid #50C878",
                                      borderBottomWidth: 3,
                                      background: "#fff",
                                      color: "#3a9d5c",
                                      cursor: "pointer",
                                      transition: "transform 80ms ease, border-bottom-width 80ms ease",
                                      flexShrink: 0,
                                    }}
                                    onMouseDown={(e) => {
                                      e.currentTarget.style.transform = "translateY(2px)";
                                      e.currentTarget.style.borderBottomWidth = "1.5px";
                                    }}
                                    onMouseUp={(e) => {
                                      e.currentTarget.style.transform = "";
                                      e.currentTarget.style.borderBottomWidth = "3px";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = "";
                                      e.currentTarget.style.borderBottomWidth = "3px";
                                    }}
                                  >
                                    <FiExternalLink size={15} />
                                  </button>
                                  <button
                                    onClick={() => navigate(`/lessons/${lesson.id}/edit`)}
                                    title="Edit lesson"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: 34,
                                      height: 34,
                                      borderRadius: 10,
                                      border: "1.5px solid #e9c46a",
                                      borderBottomWidth: 3,
                                      background: "#fff",
                                      color: "#b8890a",
                                      cursor: "pointer",
                                      transition: "transform 80ms ease, border-bottom-width 80ms ease",
                                      flexShrink: 0,
                                    }}
                                    onMouseDown={(e) => {
                                      e.currentTarget.style.transform = "translateY(2px)";
                                      e.currentTarget.style.borderBottomWidth = "1.5px";
                                    }}
                                    onMouseUp={(e) => {
                                      e.currentTarget.style.transform = "";
                                      e.currentTarget.style.borderBottomWidth = "3px";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = "";
                                      e.currentTarget.style.borderBottomWidth = "3px";
                                    }}
                                  >
                                    <FiEdit2 size={15} />
                                  </button>
                                  <button
                                    onClick={() => handleArchiveToggle(lesson.id, lesson.archived)}
                                    title={lesson.archived ? "Restore lesson" : "Archive lesson"}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: 34,
                                      height: 34,
                                      borderRadius: 10,
                                      border: "1.5px solid #9b59b6",
                                      borderBottomWidth: 3,
                                      background: "#fff",
                                      color: "#9b59b6",
                                      cursor: "pointer",
                                      transition: "transform 80ms ease, border-bottom-width 80ms ease",
                                      flexShrink: 0,
                                    }}
                                    onMouseDown={(e) => {
                                      e.currentTarget.style.transform = "translateY(2px)";
                                      e.currentTarget.style.borderBottomWidth = "1.5px";
                                    }}
                                    onMouseUp={(e) => {
                                      e.currentTarget.style.transform = "";
                                      e.currentTarget.style.borderBottomWidth = "3px";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = "";
                                      e.currentTarget.style.borderBottomWidth = "3px";
                                    }}
                                  >
                                    {lesson.archived ? <FiRotateCcw size={15} /> : <FiArchive size={15} />}
                                  </button>
                                  <button
                                    onClick={() => handleDelete(lesson.id, lesson.title)}
                                    title="Delete lesson"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: 34,
                                      height: 34,
                                      borderRadius: 10,
                                      border: "1.5px solid #e76f51",
                                      borderBottomWidth: 3,
                                      background: "#fff",
                                      color: "#e76f51",
                                      cursor: "pointer",
                                      transition: "transform 80ms ease, border-bottom-width 80ms ease",
                                      flexShrink: 0,
                                    }}
                                    onMouseDown={(e) => {
                                      e.currentTarget.style.transform = "translateY(2px)";
                                      e.currentTarget.style.borderBottomWidth = "1.5px";
                                    }}
                                    onMouseUp={(e) => {
                                      e.currentTarget.style.transform = "";
                                      e.currentTarget.style.borderBottomWidth = "3px";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = "";
                                      e.currentTarget.style.borderBottomWidth = "3px";
                                    }}
                                  >
                                    <FiTrash2 size={15} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </tbody>
                </table>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}

      <DocViewerModal
        open={!!viewDoc}
        onClose={() => setViewDoc(null)}
        path={viewDoc?.path}
        data={viewDoc?.data}
      />

      <VideoExportModal
        lesson={exportLesson}
        onClose={() => setExportLesson(null)}
      />
    </div>
  );
}

export default Lessons;
